import { CourseworkRefusal } from "@/components/assignments/CourseworkRefusal";
import { a3Copy } from "@/components/assignments/a3-copy";
import { useMemo } from "react";
import { ExternalLink } from "lucide-react";

import { assignmentNumber } from "@/components/assignments/assignment-workspace-context";
import { Button } from "@/components/ui/button";
import { Bento } from "@/components/ui/box";
import type { Box, FactBox, ListBox, Read } from "@/components/ui/box.schema";
import type { ClassroomCoursework, ClassroomSubmission } from "@/lib/api/classroom";
import type { EnrollmentStatus } from "@/lib/api/enrollments";
import { ENROLLMENT_STATUS_LABEL } from "@/lib/enrollment-status";
import { safeHref } from "@/lib/safe-href";
import { formatInCourseZone } from "@/lib/session-portal";

/**
 * A Classroom assignment the retired teaching loop recorded (ADR 0026 as
 * amended 2026-10-19, option C). It is read-only history: no command, no
 * refresh, no link into Classroom and no Google Doc route; Drive readings
 * stay. Its facts are the last read's and it says so: each stored record
 * states its last successful read, and a later failed read beside it.
 */
export type ClassroomPerspective = "teaching" | "learning";

function classroomStateLabel(state: string): string {
  return state.toLowerCase().replaceAll("_", " ").replace(/^./, first => first.toUpperCase());
}

type ExactSubmission = ClassroomSubmission & {
  learner_display_name?: string;
  enrollment_status?: EnrollmentStatus;
  destination?: { href: string; label?: string };
  copy_title?: string | null;
};

type StoredRead = { last_successful_at: string | null; unavailable_at: string | null };

/**
 * A stored record's reads, in the course zone: "Last read" when its last read
 * succeeded; when a later read failed, the last successful read beside that
 * failure.
 */
function readLines(record: StoredRead, timezone: string): string[] {
  const at = record.last_successful_at;
  const failedAt = record.unavailable_at;
  const when = (value: string) => formatInCourseZone(value, timezone, { dateStyle: "medium", timeStyle: "short" });
  const failed = failedAt && (!at || Date.parse(failedAt) >= Date.parse(at)) ? failedAt : null;
  return [at && `${failed ? a3Copy.lastSuccessfulRead : a3Copy.lastRead} ${when(at)}`, failed && a3Copy.lastReadFailed(when(failed))].filter((line): line is string => !!line);
}

function History({ submission, timezone }: { submission: ExactSubmission; timezone: string }) {
  return <details className="border-t border-rule pt-4"><summary className="cursor-pointer text-ui font-semibold">{a3Copy.submissionHistory}</summary><div className="grid gap-3 pt-4">{submission.history?.length ? submission.history.map((entry, index) => <p key={index} className="text-ui">{classroomStateLabel(entry.state)} · {formatInCourseZone(entry.at, timezone, { dateStyle: "medium", timeStyle: "short" })}</p>) : <p className="text-ui text-muted-foreground">{a3Copy.noSubmissionHistoryWasRecorded}</p>}</div></details>;
}

function BriefBody({ work, timezone }: { work: ClassroomCoursework; timezone: string }) {
  return <div className="grid gap-3">
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-meta text-muted-foreground">{work.due_at && <p>{a3Copy.due4}{formatInCourseZone(work.due_at, timezone, { dateStyle: "medium", timeStyle: "short", timeZoneName: "short" })}</p>}{work.maxPoints !== undefined && <p>{assignmentNumber(work.maxPoints)}{a3Copy.points2}</p>}{work.posted_read_at && <p>{a3Copy.publicationObserved}{formatInCourseZone(work.posted_read_at, timezone, { dateStyle: "medium", timeStyle: "short" })}</p>}</div>
    {!!work.materials?.length && <section aria-label={a3Copy.assignmentMaterials} className="grid gap-2"><h3 className="text-section font-semibold">{a3Copy.readingsAndMaterials}</h3><div>{work.materials.map((material, index) => {
      // Classroom supplies these addresses and IMAM stores them as read, so only a web link becomes one.
      const href = safeHref(material.driveFile?.alternateLink ?? material.link?.url);
      return <div key={index} className="flex flex-wrap items-center justify-between gap-3 border-t border-rule py-3"><span dir="auto">{material.driveFile?.title ?? material.link?.title ?? a3Copy.assignmentMaterial}</span>{href && <Button asChild size="sm" variant="outline"><a href={href} target="_blank" rel="noopener noreferrer">{a3Copy.openMaterial}<ExternalLink aria-hidden="true" className="size-3.5" strokeWidth={1.75} /></a></Button>}</div>;
    })}</div></section>}
  </div>;
}

function storedRead<T>(value: T, record: StoredRead, refresh: () => void, timezone: string): Read<T> {
  const at = record.last_successful_at;
  if (!at) return { status: "unread", sentence: a3Copy.notReadFromClassroomYet };
  if (record.unavailable_at && Date.parse(record.unavailable_at) >= Date.parse(at)) return {
    status: "stale", last: value, readAt: formatInCourseZone(at, timezone, { dateStyle: "medium", timeStyle: "short" }), sentence: a3Copy.theLatestReadFailedALaterChangeIsNot, refresh,
  };
  return { status: "ready", value, readAt: formatInCourseZone(at, timezone, { dateStyle: "medium", timeStyle: "short" }) };
}

/**
 * One stored submission's facts. The teaching list already says the state, so
 * the selected learner's tile leaves it out; a read the brief already states,
 * word for word, is not said again.
 */
function SubmissionFacts({ submission, work, timezone }: { submission: ExactSubmission; work: ClassroomCoursework; timezone: string }) {
  const briefReads = new Set(readLines(work, timezone));
  const reads = readLines(submission, timezone).filter(line => !briefReads.has(line));
  return <div className="grid justify-items-start gap-2">
    <p className="text-ui font-medium">{submission.copy_title ?? a3Copy.personalDocument}</p>
    {submission.assignedGrade !== undefined && <p className="text-ui">{a3Copy.classroomGrade}{assignmentNumber(submission.assignedGrade)}</p>}
    {submission.draftGrade !== undefined && submission.assignedGrade === undefined && <p className="text-ui">{a3Copy.classroomDraftGrade}{assignmentNumber(submission.draftGrade)}</p>}
    {reads.map(line => <p key={line} className="text-meta text-muted-foreground">{line}</p>)}
  </div>;
}

export interface ClassroomAssignmentRecordProps {
  work: ClassroomCoursework;
  perspective: ClassroomPerspective;
  selectedSubmissionId?: string | null;
  onSelectionChange?: (submissionId: string, href?: string) => void;
  timezone?: string;
  onRefresh?: () => void;
}

export function ClassroomAssignmentRecord({ work, perspective, selectedSubmissionId, onSelectionChange, timezone = "UTC", onRefresh = () => undefined }: ClassroomAssignmentRecordProps) {
  const submissions = useMemo(() => (work.submissions ?? []) as ExactSubmission[], [work.submissions]);
  const own = work.my_submission as ExactSubmission | null | undefined;
  // A denied exact address is never replaced by a different learner's row.
  if (selectedSubmissionId && (perspective === "learning" ? own?.institutional_id !== selectedSubmissionId : !submissions.some(row => row.institutional_id === selectedSubmissionId))) {
    return <CourseworkRefusal id="classroom-history-refusal"><p>{a3Copy.thatStoredRecordIsUnavailable}</p><Button data-act="exit" variant="subtle" onClick={() => onSelectionChange?.("", "?view=coursework")}>{a3Copy.backToCoursework}</Button></CourseworkRefusal>;
  }
  const brief: FactBox<ClassroomCoursework> = {
    kind: "fact", id: `classroom-brief-${work.institutional_id}`, job: a3Copy.readTheExactStoredClassroomInstructionsAndAttachments,
    label: a3Copy.assignmentBrief, owner: { system: "classroom", readAt: work.last_successful_at ? formatInCourseZone(work.last_successful_at, timezone, { dateStyle: "medium", timeStyle: "short" }) : null },
    read: storedRead(work, work, onRefresh, timezone), fold: "never", detail: value => value.title,
    prose: value => value.description ?? a3Copy.noAdditionalInstructions, body: value => <BriefBody work={value} timezone={timezone} />,
  };
  const rows = perspective === "learning" ? own ? [own] : [] : submissions;
  const records: ListBox<ExactSubmission[], ExactSubmission> = {
    kind: "list", id: `classroom-learners-${work.institutional_id}`, job: a3Copy.readEachAuthorizedLearnerSStoredClassroomWorkInside,
    label: perspective === "learning" ? a3Copy.yourHistoricalClassroomWork : a3Copy.learners, owner: { system: "classroom", readAt: work.last_successful_at ? formatInCourseZone(work.last_successful_at, timezone, { dateStyle: "medium", timeStyle: "short" }) : null },
    read: !rows.length && work.last_successful_at ? { status: "empty", sentence: perspective === "learning" ? a3Copy.noSubmissionRecorded : a3Copy.noLearnerSubmissionsWereRecorded } : storedRead(rows, work, onRefresh, timezone),
    fold: "never", detail: value => a3Copy.storedSubmissions(value.length),
    state: () => ({ class: "done", word: a3Copy.storedClassroomRecords }), rows: value => value,
    row: {
      key: row => row.institutional_id,
      title: row => perspective === "learning" ? row.copy_title ?? a3Copy.yourWork : row.learner_display_name ?? a3Copy.learnerSubmission,
      state: row => row.enrollment_status && row.enrollment_status !== "enrolled" ? { class: "quiet", word: ENROLLMENT_STATUS_LABEL[row.enrollment_status] } : null,
      facts: row => classroomStateLabel(row.state),
      act: () => ({ does: "open", verb: a3Copy.readStoredWork }),
      startsOpen: row => perspective === "learning" || row.institutional_id === selectedSubmissionId,
      detail: row => <div data-classroom-submission-id={row.institutional_id} className="grid gap-3">
        <SubmissionFacts submission={row} work={work} timezone={timezone} />
        {row.assignedGrade !== undefined && <p className="text-meta text-muted-foreground">{a3Copy.institutionalResultsAreApprovedSeparately}</p>}
        <History submission={row} timezone={timezone} />
      </div>,
    },
  };
  const events = { onRowOpenChange: (_boxId: string, key: string, open: boolean) => {
    if (!open || key === selectedSubmissionId) return;
    const row = rows.find(item => item.institutional_id === key);
    if (row) onSelectionChange?.(key, row.destination?.href);
  } };
  return <article data-classroom-coursework-id={work.institutional_id}><Bento {...events} id={`classroom-history-${work.institutional_id}`} journey="classroom-history" boxes={[brief, records] as Box[]} /></article>;
}
